<template>
  <nav-header :title="title"></nav-header>
  <main class="instructions-page">
    <div class="container instructions-container">
      <div class="instructions-intro">
        <span class="instructions-intro-icon" aria-hidden="true">
          <i class="material-icons">menu_book</i>
        </span>
        <div>
          <h1>{{ $englishPreferred ? "Guide" : "Vejledning" }}</h1>
          <p>
            {{
              $englishPreferred
                ? "Choose a topic below to read more."
                : "Vælg et emne herunder for at læse mere."
            }}
          </p>
        </div>
      </div>
      <div class="accordion instructions-accordion" id="accordionExample">
      <div class="accordion-item">
        <h2 class="accordion-header" id="headingOne">
          <button
            class="accordion-button accordion-button-lg"
            :class="{ collapsed: activeSection !== 'one' }"
            type="button"
            :aria-expanded="activeSection === 'one'"
            aria-controls="collapseOne"
            @click="toggleSection('one')"
          >
            Om spontantællinger
          </button>
        </h2>
        <div
          id="collapseOne"
          class="accordion-panel"
          :class="{ 'is-open': activeSection === 'one' }"
          aria-labelledby="headingOne"
          :aria-hidden="activeSection !== 'one'"
          :inert="activeSection !== 'one'"
          role="region"
        >
          <div class="accordion-body">
            <h4>Formålet med spontantællingerne</h4>
            Spontantællingerne har til formål at indsamle data om de danske
            fuglebestande og skal anvendes i forbindelse med beskyttelse af
            Danmarks fugle og natur og i videnskabelige sammenhænge. Ligesom
            punkttællingerne fokuserer spontantællingerne på almindelige
            fuglearter med hovedfokus på art og antal. Hvor punkttællingerne
            bruges til at belyse bestandsudviklingen fra år til år, anvender vi
            spontantællingerne, fx når vi ønsker at vide, hvordan
            bestandsudviklingen er for en enkelt art i løbet af året, eller
            hvordan det her og nu står til med fuglelivet i en bestemt naturtype
            eller landsdel. <br /><br />
            <span>
              <h4>Din indsats</h4>
              Du kan deltage i spontantælling hvor og hvornår, du vil. En
              tælling finder sted på et punkt, foretages af én observatør, og
              varer 5 minutter, hverken mere eller mindre. Det er således enkelt
              og overkommeligt for den enkelte deltager, men tilsammen skaber
              tællingerne enestående viden om det danske fuglelivs aktuelle
              bruger af DOFbasen. Er du ikke det, kan du oprette dig
              <strong
                ><a target="_blank" href="https://dofbasen.dk/opretmig.php"
                  >her</a
                ></strong
              >
            </span>
            <br /><br />
            <span>
              <h4>Krav til fuglekendskab</h4>
              Spontantællinger kræver godt kendskab til landets fugle (dog ikke
              nødvendigvis de sjældne). Kravet til fuglekendskab er således lidt
              højere end det grundlæggende kendskab, der kræves til f.eks.
              punkttællinger (<strong
                ><a
                  target="_blank"
                  href="https://www.dof.dk/fakta-om-fugle/punkttaellingsprogrammet"
                  >læs mere her</a
                ></strong
              >). Det skyldes groft sagt, at enhver spontantælling sammenlignes
              med mange andre spontantællinger udført af andre tællere, mens
              punkttællinger sammenlignes med tidligere punkttællinger udført af
              samme deltager. Man behøver ikke have deltaget i
              punkttællingsprojektet for at blive spontantæller. Alle med et
              godt fuglekendskab opfordres til at deltage.
            </span>
          </div>
        </div>
      </div>
      <div class="accordion-item">
        <h2 class="accordion-header" id="headingTwo">
          <button
            class="accordion-button accordion-button-lg"
            :class="{ collapsed: activeSection !== 'two' }"
            type="button"
            :aria-expanded="activeSection === 'two'"
            aria-controls="collapseTwo"
            @click="toggleSection('two')"
          >
            Praktisk udførelse af spontantællinger
          </button>
        </h2>
        <div
          id="collapseTwo"
          class="accordion-panel"
          :class="{ 'is-open': activeSection === 'two' }"
          aria-labelledby="headingTwo"
          :aria-hidden="activeSection !== 'two'"
          :inert="activeSection !== 'two'"
          role="region"
        >
          <div class="accordion-body">
            <h4>Valg af tællepunkt</h4>
            <p>
              Du må udføre spontantælling hvor og hvornår, du vil, men vi vil
              gerne have foretaget tællinger over hele landet, så vi opfordrer
              til, at man opsøger områder med ingen eller få tællinger. Du kan
              få overblik over, hvor der er få tællinger på det seneste ved at
              besøge
              <a href="https://www.dofbasen.dk/point/index.php?map=danmark"
                >dæknings-/pointkortet</a
              >.
            </p>
            <p>
              Vi ønsker tællingerne foretaget så bredt som muligt, så de
              repræsenterer hele Danmark, alle naturtyper, hele året og hele
              døgnet. Vi er eksempelvis meget interesseret i punkter i
              agerlandet, da det er landets mest udbredte landskabstype, men
              også i mere fåtallige naturtyper som f.eks. heder, der er en
              underrapporteret landskabstype, hvad fugle angår. Naturforholdene
              på punktet skal dog IKKE beskrives af tælleren.
            </p>
            <p>
              Du kan udføre en spontantælling med eller uden kikkert, men undgå
              steder med meget begrænset udsyn. Punktet må dog gerne ligge i
              f.eks. by eller skov, selvom udsynet kan være begrænset her. Du
              kan som sagt altid tælle hvor og hvornår, du vil, men tællepunkter
              i rene naturtyper giver bedre muligheder for dataanalyse og
              mindsker desuden risikoen for, at man dækker flere
              DOFbaselokaliteter under spontantællingen.
            </p>
            <h4>Hvilke fugle skal tælles?</h4>
            <p>
              Alle de fugle, der ses eller høres fra optællingspunktet, og som
              kan artsbestemmes, inden for ca. 1 km fra tællepunktet efter din
              bedste vurdering. Du må IKKE bruge teleskop. Du må ikke påbegynde
              en spontantælling foranlediget af en allerede opdaget fugl, f.eks.
              en sjælden art. Dette for at undgå, at usædvanlige arter bliver
              overrepræsenteret. Hvis det ikke er muligt at artsbestemme eller
              notere samtlige fugle du ser inden de fem minutter er gået, er det
              tilladt at fortsætte indtastning efter tiden er gået. Sørg så vidt
              muligt for kun at indtaste fugle, der blev registreret inden for
              de fem minutter.
            </p>
            <h4>Hvornår på døgnet kan spontantællinger foretages?</h4>
            <p>
              Spontantællinger kan foretages hele døgnet. Fuglene er som regel
              mest aktive først på dagen, men du kan frit vælge at tælle på det
              tidspunkt, der passer dig bedst.
            </p>
            <h4>Vejrforhold</h4>
            <p>
              Spontantællinger bør kun udføres i godt vejr. Regn og tæt tåge bør
              undgås. Vejrforholdene kan indtastes, men det er ikke obligatorisk
              at gøre det.
            </p>
          </div>
        </div>
      </div>
      <div class="accordion-item">
        <h2 class="accordion-header" id="headingFour">
          <button
            class="accordion-button accordion-button-lg"
            :class="{ collapsed: activeSection !== 'four' }"
            type="button"
            :aria-expanded="activeSection === 'four'"
            aria-controls="collapseFour"
            @click="toggleSection('four')"
          >
            Indtastning af spontantællinger
          </button>
        </h2>
        <div
          id="collapseFour"
          class="accordion-panel"
          :class="{ 'is-open': activeSection === 'four' }"
          aria-labelledby="headingFour"
          :aria-hidden="activeSection !== 'four'"
          :inert="activeSection !== 'four'"
          role="region"
        >
          <div class="accordion-body">
            <b>Indtastning:</b> Sker bedst ved brug af den særlige
            spontantællings-app, som findes på
            <a href="https://spontan.dofbasen.dk">spontan.dofbasen.dk</a>. Fra
            hjemmeside kan appen installeres på telefonen, så den også kan
            bruges offline. <br />Du kan lave indtastning direkte i felten via
            ny tælling” eller indtaste tællinger efterfølgende via ”Indtast
            gennemført tælling”. <br /><br />
            Ved live-indtastning vælges dato og starttid automatisk af app’en.
            Placeringen angives via telefonens GPS-signal eller ved at brugeren
            selv placerer ’nålen’. Nålen skal sættes der, hvorfra brugeren står
            stille og laver sin tælling.
            <br /><br />
            Efter endt tælling vælger appen selv den mest præcise
            DOFbaselokalitet. <br />Hvis der er mangler lokalitetsgrænser i
            området, eller der er begrænset forbindelse til internettet, vælger
            appen selv lokaliteten med den nærmeste centerkoordinat. Der er
            siden mulighed for at ændre den valgte lokalitet <br /><br />
            Det er muligt, men ikke nødvendigt, at indtaste detaildata såsom køn
            og alder. Disse detaildata må ikke tage fokus fra grunddata, dvs.
            art, antal og primær adfærd. Nye individer af samme art, der
            registreres i løbet af de 5 minutter, tilføjes blot til det allerede
            indtastede antal for arten.
            <br /><br />
            Hvis en spontantælling ikke indtastes i appen under tællingen, men i
            stedet fx noteres i en notesbog, bør tællingen indtastes i appen
            snarest muligt efter tællingen. I så fald indtastes datoen og
            starttiden (sluttiden sættes automatisk til 5 min senere).
            <br /><br />
            <b>Varigheden af en spontantælling:</b> 5 minutter, hverken mere
            eller mindre. Appen til spontantælling indeholder et stopur, der
            sikrer den korrekte varighed. Ved indtastning aktiveres der på
            skærmen en tidstælleknap, der starter med at tælle ned nogle
            sekunder, og som derefter holder styr på de 5 minutter. Efter 5
            minutter starter alarmen, men uret fortsætter med at tælle ned,
            altså i minus. HUSK at slå lyd/vibration til på mobiltelefonen!
            Appen skal være åben og mobilen ulåst for at alarmen fungerer.
            <br /><br />
            En tælling kan ikke afbrydes og derefter genoptages; hvis tællingen
            afbrydes, er den gået tabt og må slettes. Men start meget gerne
            forfra, det tager kun 5 minutter!
          </div>
        </div>
      </div>
      <div class="accordion-item">
        <h2 class="accordion-header" id="headingFive">
          <button
            class="accordion-button accordion-button-lg"
            :class="{ collapsed: activeSection !== 'five' }"
            type="button"
            :aria-expanded="activeSection === 'five'"
            aria-controls="collapseFive"
            @click="toggleSection('five')"
          >
            Spiltilstand
          </button>
        </h2>
        <div
          id="collapseFive"
          class="accordion-panel"
          :class="{ 'is-open': activeSection === 'five' }"
          aria-labelledby="headingFive"
          :aria-hidden="activeSection !== 'five'"
          :inert="activeSection !== 'five'"
          role="region"
        >
          <div class="accordion-body">
            Spil-elementet i appen skal gøre det sjovere for brugeren at besøge
            områder, der normalt ikke tælles fugle i og belønne denne indsats!
            <br />Spiltilstand kan når som helst slås til/fra under menupunktet
            indstillinger. <br /><br />
            Beregningen af antal point for en tælling tager udgangspunkt i antal
            tællinger i kvadratet i den seneste måned. Point gives kun for en
            tælling højst én gang i timen i samme kvadrat, og efter den første
            sådanne tælling gives der kun 10 point for øvrige tællinger samme
            dag i samme kvadrat for hver bruger. Der er desuden en tidsgrænse på
            14 dage for hvor længe efter en tælling, man må være om at indtaste
            den, hvis man vil have points for den. Du kan se dine point og din
            placering under menupunktet ’Min Statistik’ på forsiden.
          </div>
        </div>
      </div>
      <div class="accordion-item">
        <h2 class="accordion-header" id="headingSix">
          <button
            class="accordion-button accordion-button-lg"
            :class="{ collapsed: activeSection !== 'six' }"
            type="button"
            :aria-expanded="activeSection === 'six'"
            aria-controls="collapseSix"
            @click="toggleSection('six')"
          >
            Spørgsmål og kontakt
          </button>
        </h2>
        <div
          id="collapseSix"
          class="accordion-panel"
          :class="{ 'is-open': activeSection === 'six' }"
          aria-labelledby="headingSix"
          :aria-hidden="activeSection !== 'six'"
          :inert="activeSection !== 'six'"
          role="region"
        >
          <div class="accordion-body">
            Vil du vide mere om spontantællingerne, kan du læse mere i DOF’s
            publikationer og på DOF’s hjemmeside
            <a href="https://www.dof.dk/fakta-om-fugle/spontantaellinger"
              >www.DOF.dk/fakta-om-fugle/spontantællinger</a
            >. <br />Du er altid velkommen til at kontakte de projektansvarlige
            i Fuglenes Hus: <br /><br />Thomas Vikstrøm (<a
              href="mailto: thomas.vikstroem@dof.dk"
              >thomas.vikstroem@dof.dk</a
            >) & Daniel Palm Eskildsen (<a href="mailto: dpe@dof.dk"
              >dpe@dof.dk</a
            >) <br />Fuglenes Hus <br />Vesterbrogade 140 <br />1620 København
            V. <br />Tlf.: 3328 3800 (kl. 11-15)

            <br /><br />© Dansk Ornitologisk Forening 2021

            <br /><br />
          </div>
        </div>
      </div>
      <div class="accordion-item">
        <h2 class="accordion-header" id="headingSeven">
          <button
            class="accordion-button orange-header accordion-button-lg"
            :class="{ collapsed: activeSection !== 'seven' }"
            type="button"
            :aria-expanded="activeSection === 'seven'"
            aria-controls="collapseSeven"
            @click="toggleSection('seven')"
          >
            Fugle i Ny Natur
          </button>
        </h2>
        <div
          id="collapseSeven"
          class="accordion-panel"
          :class="{ 'is-open': activeSection === 'seven' }"
          aria-labelledby="headingSeven"
          :aria-hidden="activeSection !== 'seven'"
          :inert="activeSection !== 'seven'"
          role="region"
        >
          <div class="accordion-body">
            <h1>FiNN-tællinger</h1>

          <p>
            FiNN-tællingerne er en registreringsmetode, som benyttes i projektet 
            <strong>Fugle i Ny Natur (FiNN)</strong>.
          </p>

          <p>
            Inden du går i gang med tællingerne, bør du læse om projektet her: 
            <a href="https://www.dof.dk/naturbeskyttelse/projekter/fugle-i-ny-natur-finn" target="_blank">
              https://www.dof.dk/naturbeskyttelse/projekter/fugle-i-ny-natur-finn
            </a>
          </p>

          <p>
            Tællingerne benyttes i områder knyttet til <strong>FiNN</strong>.
            Mange vil kende til spontantællingerne, som har været en del af DOFbasen siden marts 2023.
            FiNN-tællingerne er sådan set ”blot” spontantællinger foretaget inden for et FiNN-område og under den særlige FiNN-sektion i spontantællingsappen.
          </p>

          <p>
            Det er vores erfaring, at denne type tælling er nem for den deltagende at foretage, 
            ligesom det hurtigt bliver en hyggelig vane at tage sig fem minutter til at registrere fuglene omkring sig.
          </p>

          <h2>Kort sagt går FiNN-tællinger ud på følgende:</h2>
          <ul>
            <li>I spontantællingsappen findes ”FiNN-sektionen”.</li>
            <li>Find et FiNN-område nær dig via FiNN-kortet.</li>
            <li>Du vælger selv placeringen inden for FiNN-området. Her skal du blive de næste 5 minutter.</li>
            <li>Tællingen startes efter automatisk valgt placering (via indbygget GPS) og varer i alt fem minutter.</li>
            <li>Under de fem minutter registreres alle fugle, som ses og høres inden for en afstand af ca. 1 km. Fokus skal dog være på det nære.</li>
            <li>
              Der registreres antal og såkaldt primær adfærdskode på alle observationer (fx ”Rastende” eller ”Overflyvende”). 
              Øvrige data som sekundær adfærdskode, tid mv. kan registreres der, hvor man har tid i overskud, 
              men fokus bør være på at få alle fugle med. Det er også muligt at tilføje ekstra info efter de fem minutter er gået.
            </li>
            <li>Tællingen afrundes, når du er klar, og ligger derefter på DOFbasen under den lokalitet, som FiNN-området findes i.</li>
          </ul>

          <p>
            Dine data bidrager dermed til projektet. Nemt!
          </p>

          <p>
            Det er muligt i appen at se, hvor der førhen er foretaget tællinger inden for områdeafgrænsningen. 
            Lav gerne halvdelen af tællingerne fra stier og veje og den anden halvdel væk fra disse.
          </p>

          <p>
            Man kan i øvrigt deltage via notesbog og medbragt stopur, hvis ikke man ønsker at benytte sig af en mobiltelefon under tællingerne.
            Benyt funktionen <em>"Indtast gennemført FiNN-tælling"</em>.
          </p>

          <p>
            Kravet til FiNN-tællinger er godt kendskab til landets fugle (dog ikke nødvendigvis de sjældne).
            Derudover kræver deltagelse et login til DOFbasen.
          </p>

          <p>
            Kniber det med at få den ned på telefonen, kan du finde videoguides til installation på
            <a target="_blank" href="https://www.dof.dk/naturbeskyttelse/projekter/fugle-i-ny-natur-finn">denne side</a>
            under ”Sådan henter du appen”.
          </p>
          </div>
        </div>
      </div>
      </div>
    </div>
  </main>
</template>

<script>
import NavHeader from "@/components/navbars/NavHeader.vue";

export default {
  components: {
    NavHeader,
  },
  data() {
    return {
      activeSection: null,
      title: this.$englishPreferred ? "Guide" : "Vejledning",
    };
  },
  methods: {
    toggleSection(section) {
      this.activeSection = this.activeSection === section ? null : section;
    },
  },
};

</script>

<style scoped>
:deep(#navbar) {
  margin-bottom: 0 !important;
}

.instructions-page {
  background:
    radial-gradient(circle at 8% 0%, rgb(31 122 175 / 10%), transparent 24rem),
    radial-gradient(circle at 94% 10%, rgb(81 175 48 / 9%), transparent 22rem),
    #f5f8f9;
  min-height: calc(100vh - 8rem);
  padding: 1.5rem 0 3rem;
}

.instructions-container {
  max-width: 940px;
}

.instructions-intro {
  align-items: center;
  display: flex;
  gap: 1rem;
  margin-bottom: 1.25rem;
}

.instructions-intro-icon {
  align-items: center;
  background: linear-gradient(135deg, #1f7aaf, #51af30);
  border-radius: 1rem;
  box-shadow: 0 7px 18px rgb(31 122 175 / 20%);
  color: #fff;
  display: flex;
  flex: 0 0 auto;
  height: 3.25rem;
  justify-content: center;
  width: 3.25rem;
}

.instructions-intro-icon .material-icons {
  font-size: 1.65rem;
}

.instructions-intro h1 {
  color: #174f70;
  font-size: clamp(1.4rem, 4vw, 1.8rem);
  font-weight: 700;
  margin: 0 0 0.15rem;
}

.instructions-intro p {
  color: #667781;
  margin: 0;
}

.instructions-accordion {
  display: grid;
  gap: 0.75rem;
}

.accordion-item {
  border: 1px solid rgb(31 122 175 / 12%);
  border-radius: 1rem !important;
  box-shadow: 0 6px 20px rgb(35 77 98 / 9%);
  overflow: hidden;
}

.accordion-header {
  margin: 0;
}

.accordion-button {
  background: #fff;
  border: 0;
  border-left: 4px solid #1f7aaf;
  box-shadow: none !important;
  color: #174f70 !important;
  line-height: 1.3;
  min-height: 4.4rem;
  padding: 1rem 1.25rem;
  transition:
    background-color 180ms ease,
    color 180ms ease;
}

.accordion-button:hover,
.accordion-button:focus-visible {
  background: #f0f7fb;
}

.accordion-button:not(.collapsed) {
  background: linear-gradient(120deg, #1f7aaf, #2888b5);
  color: #fff !important;
}

.accordion-button::after {
  background-image: none !important;
  border-bottom: 2px solid currentColor;
  border-right: 2px solid currentColor;
  content: "";
  flex: 0 0 auto;
  height: 0.65rem;
  margin-left: auto;
  transform: rotate(45deg);
  transition: transform 180ms ease;
  width: 0.65rem;
}

.accordion-button:not(.collapsed)::after {
  transform: rotate(225deg);
}

.orange-header {
  border-left-color: #ff8800;
  color: #b65f00 !important;
}

.orange-header:hover,
.orange-header:focus-visible {
  background: #fff6eb;
}

.orange-header:not(.collapsed) {
  background: linear-gradient(120deg, #ff8800, #f39a2d);
  color: #fff !important;
}

.accordion-button-lg {
  font-size: 1.08rem;
  font-weight: 700;
}

.accordion-panel {
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows 280ms ease;
}

.accordion-panel > .accordion-body {
  color: #344953;
  font-size: 1rem;
  line-height: 1.7;
  min-height: 0;
  overflow: hidden;
  padding: 0 1.35rem;
  transition: padding 280ms ease;
}

.accordion-panel.is-open {
  grid-template-rows: 1fr;
}

.accordion-panel.is-open > .accordion-body {
  padding: 1.35rem;
}

.accordion-body :deep(h1),
.accordion-body :deep(h2),
.accordion-body :deep(h4) {
  color: #174f70;
  font-weight: 700;
  line-height: 1.3;
}

.accordion-body :deep(h1) {
  font-size: 1.6rem;
}

.accordion-body :deep(h2) {
  font-size: 1.25rem;
  margin-top: 1.5rem;
}

.accordion-body :deep(h4) {
  font-size: 1.08rem;
  margin-top: 1.25rem;
}

.accordion-body :deep(a) {
  overflow-wrap: anywhere;
}

.accordion-body :deep(li + li) {
  margin-top: 0.45rem;
}

@media (max-width: 640px) {
  .instructions-page {
    padding-bottom: 2rem;
  }

  .instructions-intro {
    padding: 0 0.15rem;
  }

  .accordion-button {
    min-height: 4rem;
    padding: 0.9rem 1rem;
  }

  .accordion-button-lg {
    font-size: 1rem;
  }

  .accordion-panel.is-open > .accordion-body {
    padding: 1.1rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .accordion-button,
  .accordion-button::after,
  .accordion-panel,
  .accordion-panel > .accordion-body {
    transition: none;
  }
}
</style>
